import type {
  PlatformDefaultBundle,
  WithPlatformDefaults,
} from '@lifeomic/service-clients/fhir-client';

/** Member-operations API: `POST /v1/member-operations/members/onboard-member` (onboard member to app). */
export const POST_MEMBER_ONBOARD_APP =
  '/v1/member-operations/members/onboard-member' as const;

/**
 * Response shape for `POST /v1/member-operations/members/onboard-member`. The
 * endpoint runs the onboard flow synchronously, so failures show up as a 200
 * response with `ok: false` plus an `error` and `howToFix` payload — rather
 * than as an HTTP error — so the UI can surface the remediation step.
 */
export type OnboardMemberResponse =
  | { ok: true; messages?: string[] }
  | { ok: false; error: string; howToFix: string; messages?: string[] };

/**
 * Returns a single-line error message combining `error` + `howToFix` when the
 * response represents a failure (`ok: false`); returns `undefined` for
 * successful responses or unrecognized payloads (treated as success so we
 * don't block the UI on an unexpected shape).
 */
export function formatOnboardMemberError(
  data: unknown,
): { error: string; howToFix: string } | undefined {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const payload = data as Partial<OnboardMemberResponse>;
  if (payload.ok !== false) {
    return undefined;
  }
  const error =
    typeof payload.error === 'string' && payload.error.trim().length > 0
      ? payload.error.trim()
      : 'Unable to onboard this member.';
  const howToFix =
    typeof payload.howToFix === 'string' && payload.howToFix.trim().length > 0
      ? payload.howToFix.trim()
      : undefined;
  return { error, howToFix: howToFix ?? '' };
}

/** First segment of React Query keys for FHIR Person search used for app username (prefix match on invalidate). */
export const MEMBER_APP_PERSON_USERNAME_QUERY_KEY = [
  'member-app-person-username',
] as const;

/** Person.identifier system for the Fountain Life app username (linked Person search). */
export const MEMBER_APP_USERNAME_IDENTIFIER_SYSTEM =
  'http://lifeomic.com/fhir/username';

export function getAppUsernameFromPersonSearchBundle(
  bundle:
    | PlatformDefaultBundle<WithPlatformDefaults<fhir3.Person>>
    | undefined
    | null,
): string {
  const person = bundle?.entry?.[0]?.resource;
  if (!person?.identifier?.length) {
    return '';
  }
  const match = person.identifier.find(
    id => id.system === MEMBER_APP_USERNAME_IDENTIFIER_SYSTEM,
  );
  const raw = (match?.value ?? person.identifier[0]?.value ?? '').trim();
  return raw;
}
